import { Icon } from '@iconify/react'
import { Button } from "@/components/ui/button"
import { TypographyXSmallMuted } from '@/components/ui/typography.jsx'
import { MultiSelectCommand } from '@/pages/dashboard/library/creative/MultiSelectCommand.jsx'
import { useEffect, useState } from 'react'
import { ApproveDeleteDialog } from '@/components/dialogs/ApproveDeleteDialog.jsx'
import { motion } from 'framer-motion'
import { AnalyticsService } from '@/services/analytics.js'
import { UserRoles } from '@/consts/index.js'
import { RoleGuard } from '@/guards/RoleGuard.jsx'
import { useRoleGuard } from '@/hooks/useRoleGuard.jsx'
import { useCurrentSpace } from '@/hooks/useCurrentSpace'
import { useNavigate, useParams } from 'react-router-dom'
import { useDashboardContext } from '@/context-state/DashboardContext.jsx'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import EngagingWrapper from '@/components/engaging-wrapper'

export const CreativeProperties = ({ initialProperties = [], onPropertyChanged, type, onTagSelected, onPropertySelected, creativeId }) => {

    const { space } = useCurrentSpace()
    const [properties, setProperties] = useState([])
    const [propertyToDelete, setPropertyToDelete] = useState(null)
    const userAllowToEdit = useRoleGuard(UserRoles.EDITOR)
    const navigate = useNavigate()
    const { accountId } = useParams()
    const { scrollContext } = useDashboardContext()
    const { scrollContainerRef } = scrollContext
    const relevantProperties = type === 'image' ? space.imageProperties : space.videoProperties

    const deleteProperty = () => {
        if (!propertyToDelete) {
            return
        }
        setProperties(properties.filter(p => p.id !== propertyToDelete.id))
        setPropertyToDelete(null)
        AnalyticsService.track('property-deleted')
    }

    const updatePropertyTags = (property, newTags) => {
        setProperties(properties.map(p => p.id === property.id ? { ...p, tags: newTags } : p))
        AnalyticsService.track('property-tags-updated', { property, newTags })
    }

    useEffect(() => {
        // Debounce the update to prevent excessive re-renders
        const handler = setTimeout(() => {
            if (JSON.stringify(initialProperties) !== JSON.stringify(properties)) {
                onPropertyChanged({ properties })
            }
        }, 100)
        return () => clearTimeout(handler)
    }, [properties])

    useEffect(() => {
        // if (initialProperties.length > 0) {
        // TODO in the future if needed, filter out the tags that are not exist in the relevantProperties, that way when the user change the tags schema it will apply for all creatives and won't disaply old tags
        const initialPropertiesWithTags = relevantProperties.map(property => ({
            ...property,
            tags: initialProperties.find(p => p.id === property.id)?.tags || []
        }))
        setProperties(initialPropertiesWithTags)
        // }
    }, [initialProperties, creativeId])


    return (
        <div className="">

            <ApproveDeleteDialog
                isDialogOpen={!!propertyToDelete}
                onClose={() => setPropertyToDelete(null)}
                approveDelete={deleteProperty}
            />

            {/* Header with Configure Properties button */}
            <Button
                variant="outline"
                size="sm"
                className="text-xs absolute right-4 top-4"
                onClick={() => {
                    // Navigate to properties configuration page
                    navigate(`/account/${accountId}/space/${space._id}/dashboard/settings/space/data-config`)
                    if (scrollContainerRef?.current) {
                        scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' })
                    }
                }}
            >
                <Icon icon="lucide:settings" className="h-3 w-3 mr-1" />
                Configure Properties
            </Button>
            <div className="flex flex-col gap-1">
                {
                    properties.map((property, i) => {
                        return (
                            <div className="flex relative group items-center border-b border-gray-200 py-2" key={property.id}>

                                {/* <RoleGuard minimumRoleRequired={UserRoles.EDITOR} hideContent>
                                    <Icon
                                        icon="ph:trash"
                                        className="h-4 w-4 shrink-0 absolute -left-5 top-2.5 text-error cursor-pointer opacity-0 transition group-hover:opacity-100"
                                        onClick={() => setPropertyToDelete(property)}
                                    />
                                </RoleGuard> */}


                                <motion.div
                                    className="flex items-center h-fit relative"
                                    initial={{ opacity: 0, left: "-30px" }}
                                    animate={{ opacity: 1, left: 0 }}
                                    transition={{ duration: 0.2, delay: i * 0.02 }}
                                >
                                    <TooltipProvider delayDuration={100}>
                                        <Tooltip>
                                            <TooltipTrigger className="flex gap-1" onClick={() => onPropertySelected(property)}>
                                                <TypographyXSmallMuted className="text-primary">
                                                    {i + 1}.
                                                </TypographyXSmallMuted>
                                                <TypographyXSmallMuted className="w-28 truncate text-left text-primary hover:underline">
                                                    {property.label}
                                                </TypographyXSmallMuted>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                <Button variant="link" size="sm" onClick={() => onPropertySelected(property)}>
                                                    <Icon icon="lucide:bar-chart-3" className="h-3 w-3 mr-1" />
                                                    View split report
                                                </Button>
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                </motion.div>


                                <motion.div
                                    className="flex items-center h-fit relative"
                                    initial={{ opacity: 0, left: "-30px" }}
                                    animate={{ opacity: 1, left: 0 }}
                                    transition={{ duration: 0.2, delay: i * 0.02 }}
                                >
                                    <RoleGuard minimumRoleRequired={UserRoles.EDITOR}>
                                        <MultiSelectCommand
                                            disabled={!userAllowToEdit}
                                            onSelectedChange={(newSelectedTags) => updatePropertyTags(property, newSelectedTags)}
                                            initialSelected={property.tags}
                                            availableOptions={(relevantProperties.find(p => p.id === property.id)?.tags || [])
                                                .filter(tag => !property.tags.some(selectedTag => selectedTag.label === tag.label))
                                            }
                                            onTagClick={(tag) => onTagSelected(property, tag)}
                                        />
                                    </RoleGuard>
                                </motion.div>
                            </div>
                        )
                    })
                }

            </div>
            {/* <RoleGuard minimumRoleRequired={UserRoles.EDITOR}>
                <Button
                    className="gap-1 mt-4 p-0 hover:bg-background hover:underline"
                    size="mini"
                    variant="primary-ghost"
                    onClick={addNewProperty}
                    disabled={properties.length >= relevantProperties.length}
                >
                    <Icon icon="mage:plus" className="h-4 w-4" />
                    {properties.length >= relevantProperties.length ? 'All Properties Added' : 'Add A Property'}
                </Button>
            </RoleGuard> */}
        </div>
    )
}

